import React, { useState, useEffect, useRef } from 'react';
import jupyterhubWorkspaceService from '../../../services/jupyterhubWorkspaceService';
import config from '../../../config/domains';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip
} from '@mui/material';

// icons
import {
    IconExternalLink,
    IconRefresh,
    IconMaximize,
    IconMinimize,
    IconBrandPython as IconBrandJupyter,
    IconAlertCircle,
    IconCircleCheck
} from '@tabler/icons-react';

// ==============================|| EMBEDDED JUPYTER ||============================== //

const EmbeddedJupyter = ({ projectName, onFunctionDeploy }) => {
    const theme = useTheme();
    const iframeRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [jupyterUrl, setJupyterUrl] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [deploymentStatus, setDeploymentStatus] = useState(null);

    // Get user info from Redux or localStorage
    const userToken = localStorage.getItem('user_token') || '';
    const jupyterUsername = `${userToken}_${projectName}`.toLowerCase();

    useEffect(() => {
        // Clear any existing session first
        const initializeWorkspace = async () => {
            setLoading(true);
            setError(null);
            console.log('Initializing workspace for project:', projectName);

            // Force clear all sessions before starting
            await jupyterhubWorkspaceService.forceLogoutAll();

            try {
                // Use workspace service to handle session switching
                const iframeUrl = await jupyterhubWorkspaceService.getWorkspaceIframeUrl(projectName);

                setJupyterUrl(iframeUrl);
                console.log('JupyterHub iframe URL:', iframeUrl);
                // Don't set loading false here, wait for iframe onLoad
            } catch (error) {
                console.error('Error generating token:', error);
                console.error('Error details:', error.response?.data || error.message);
                setError(`Failed to generate authentication token: ${error.response?.data?.detail || error.message}`);
                setLoading(false);
            }
        };

        initializeWorkspace();

        // Setup message listener for Lambda deployment and workspace management
        const handleMessage = (event) => {
            // Verify origin
            // Allow messages from configured domains
            const allowedOrigins = [config.urls.main, config.urls.jupyter];
            if (!allowedOrigins.some((origin) => event.origin.startsWith(origin))) return;

            const { type, data } = event.data;

            switch (type) {
                case 'jupyter:ready':
                    setLoading(false);
                    setConnectionStatus('connected');
                    injectLambdaExtension();
                    break;

                case 'lambda:deploy':
                    handleLambdaDeploy(data);
                    break;

                case 'lambda:test':
                    handleLambdaTest(data);
                    break;

                case 'jupyter:error':
                    setError(data.message);
                    setConnectionStatus('error');
                    break;

                case 'jupyterhub:workspace-mismatch':
                    // Handle workspace mismatch
                    handleWorkspaceMismatch(data);
                    break;

                case 'jupyterhub:workspace':
                    // Verify workspace matches
                    if (data.workspace !== jupyterUsername) {
                        handleWorkspaceMismatch({
                            currentUser: data.user,
                            requestedWorkspace: jupyterUsername
                        });
                    }
                    break;

                default:
                    console.log('Unknown message type:', type);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
            // Clear session when component unmounts
            console.log('EmbeddedJupyter unmounting, clearing session');
            jupyterhubWorkspaceService.forceLogoutAll();
        };
    }, [projectName, jupyterUsername]);

    const injectLambdaExtension = () => {
        // Inject Lambda extension code into Jupyter
        const extensionCode = `
            // DataHub Lambda Extension
            console.log('Loading DataHub Lambda Extension...');
            
            window.datahub_lambda = {
                deploy: function(func, config = {}) {
                    const funcInfo = {
                        name: func.name,
                        code: func.toString(),
                        config: {
                            memory: config.memory || '256Mi',
                            timeout: config.timeout || 30,
                            dependencies: config.dependencies || []
                        }
                    };
                    
                    // Send to parent window
                    window.parent.postMessage({
                        type: 'lambda:deploy',
                        data: funcInfo
                    }, '*');
                    
                    console.log('Function deployed:', funcInfo.name);
                    return func;
                }
            };
            
            // Create IPython magic command
            IPython.CodeCell.prototype.auto_highlight = function () {
                this._auto_highlight(IPython.config.cell_magic_highlight);
            };
            
            // Register deploy magic
            Jupyter.notebook.kernel.execute(\`
def deploy(line, cell):
    """Deploy function as Lambda"""
    import json
    from IPython.display import Javascript, display
    
    # Parse function from cell
    exec(cell, globals())
    
    # Get function name from line
    func_name = line.strip() or 'lambda_function'
    
    # Send deployment message
    js_code = f'''
    window.parent.postMessage({{
        type: 'lambda:deploy',
        data: {{
            name: '{func_name}',
            code: \`{cell}\`,
            notebook: Jupyter.notebook.notebook_path
        }}
    }}, '*');
    '''
    display(Javascript(js_code))
    
    return f"Function '{func_name}' deployed!"

# Register magic command
get_ipython().register_magic_function(deploy, 'cell')
\`);
            
            console.log('DataHub Lambda Extension loaded!');
        `;

        if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage(
                {
                    type: 'execute:code',
                    code: extensionCode
                },
                '*'
            );
        }
    };

    const handleLambdaDeploy = async (functionData) => {
        setDeploymentStatus('deploying');

        try {
            // Call parent's deployment handler
            if (onFunctionDeploy) {
                await onFunctionDeploy(functionData);
            }

            setDeploymentStatus('deployed');

            // Notify Jupyter of successful deployment
            if (iframeRef.current) {
                iframeRef.current.contentWindow.postMessage(
                    {
                        type: 'lambda:deployed',
                        data: {
                            functionName: functionData.name,
                            endpoint: `/api/v1/lambda/functions/${functionData.name}/invoke`
                        }
                    },
                    '*'
                );
            }

            setTimeout(() => {
                setDeploymentStatus(null);
            }, 3000);
        } catch (error) {
            setDeploymentStatus('error');
            console.error('Deployment error:', error);
        }
    };

    const handleLambdaTest = async (testData) => {
        // Handle function testing
        console.log('Testing function:', testData);
    };

    const handleRefresh = async () => {
        setLoading(true);
        setError(null);
        setJupyterUrl(''); // Clear old URL

        try {
            // Use workspace service to handle session switching
            const iframeUrl = await jupyterhubWorkspaceService.getWorkspaceIframeUrl(projectName);

            console.log('Refreshed JupyterHub iframe URL:', iframeUrl);
            setJupyterUrl(iframeUrl);
            setLoading(false);
        } catch (error) {
            console.error('Error refreshing with token:', error);
            setError('Failed to refresh authentication. Please try again.');
            setLoading(false);
        }
    };

    const handleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleOpenInNewTab = () => {
        window.open(jupyterUrl, '_blank');
    };

    const handleWorkspaceMismatch = (data) => {
        setError(
            `Workspace access error: You are logged in as ${data.currentUser?.name || 'unknown'} but trying to access workspace ${data.requestedWorkspace}.`
        );
        setConnectionStatus('error');

        // Show dialog to switch workspace
        setDeploymentStatus('workspace-mismatch');

        // Auto logout after 5 seconds
        setTimeout(() => {
            const logoutUrl = `${config.JUPYTER_URL}/hub/logout`;
            const loginUrl = `${config.JUPYTER_URL}/hub/login`;
            const nextUrl = `/hub/user/${jupyterUsername}/lab`;

            // Open login in new tab
            window.open(`${loginUrl}?next=${encodeURIComponent(nextUrl)}`, '_blank');

            // Reset state
            setDeploymentStatus(null);
            setError('Please login in the new tab with the correct workspace credentials.');
        }, 5000);
    };

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected':
                return theme.palette.success.main;
            case 'connecting':
                return theme.palette.warning.main;
            case 'error':
                return theme.palette.error.main;
            default:
                return theme.palette.grey[500];
        }
    };

    return (
        <>
            <Card sx={{ height: isFullscreen ? '100vh' : '80vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: theme.palette.background.default
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconBrandJupyter size={24} />
                        <Typography variant="h5">JupyterLab</Typography>
                        <Chip
                            label={connectionStatus}
                            size="small"
                            sx={{
                                bgcolor: getStatusColor(),
                                color: 'white'
                            }}
                        />
                        {deploymentStatus && (
                            <Chip
                                label={
                                    deploymentStatus === 'deploying'
                                        ? 'Deploying...'
                                        : deploymentStatus === 'deployed'
                                          ? 'Deployed!'
                                          : 'Error'
                                }
                                size="small"
                                color={deploymentStatus === 'deployed' ? 'success' : deploymentStatus === 'error' ? 'error' : 'warning'}
                                icon={
                                    deploymentStatus === 'deployed' ? (
                                        <IconCircleCheck size={16} />
                                    ) : deploymentStatus === 'error' ? (
                                        <IconAlertCircle size={16} />
                                    ) : (
                                        <CircularProgress size={16} />
                                    )
                                }
                            />
                        )}
                    </Box>

                    <Box display="flex" gap={1}>
                        <Tooltip title="Refresh">
                            <IconButton size="small" onClick={handleRefresh}>
                                <IconRefresh size={18} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                            <IconButton size="small" onClick={handleFullscreen}>
                                {isFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Open in New Tab">
                            <IconButton size="small" onClick={handleOpenInNewTab}>
                                <IconExternalLink size={18} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <CardContent sx={{ flex: 1, p: 0, position: 'relative' }}>
                    {loading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'background.paper',
                                zIndex: 10
                            }}
                        >
                            <CircularProgress size={48} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Loading JupyterLab...
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                                This may take a few seconds
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            <Typography variant="body1">{error}</Typography>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                <Button size="small" variant="contained" onClick={handleRefresh}>
                                    Try Again
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={async () => {
                                        await jupyterhubWorkspaceService.forceLogoutAll();
                                        handleRefresh();
                                    }}
                                >
                                    Clear Session & Retry
                                </Button>
                            </Box>
                        </Alert>
                    )}

                    {jupyterUrl && !error && (
                        <iframe
                            ref={iframeRef}
                            src={jupyterUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                display: 'block'
                            }}
                            title="JupyterLab"
                            onLoad={() => {
                                // Initial load complete
                                setTimeout(() => {
                                    setLoading(false);
                                    setConnectionStatus('connected');
                                }, 2000);
                            }}
                            onError={() => {
                                setLoading(false);
                                setError('Failed to load JupyterLab. Please check your connection.');
                                setConnectionStatus('error');
                            }}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Fullscreen Dialog */}
            <Dialog fullScreen open={isFullscreen} onClose={() => setIsFullscreen(false)}>
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h4">JupyterLab - {projectName}</Typography>
                        <IconButton onClick={() => setIsFullscreen(false)}>
                            <IconMinimize />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <iframe
                        src={jupyterUrl}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="JupyterLab Fullscreen"
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EmbeddedJupyter;
